"use client";

import { useEffect, useRef } from "react";
import MessageSquarePlus from "lucide-react/dist/esm/icons/message-square-plus";
import { track } from "@/lib/analytics/events";
import { demandCategoryFromSlug } from "@/lib/demand-surveys/catalog";
import { logMissedSearch } from "@/lib/demand-surveys/client";
import { DemandSurveyForm } from "./DemandSurveyForm";

const MISSED_SEARCH_SETTLE_MS = 1_500;

/**
 * Shown inside the browse empty state: the visitor just told us, by searching,
 * what they want and we do not have. Ask what it is worth to them.
 */
export function EmptySearchDemandCard({
  query,
  categorySlug,
}: {
  query?: string;
  categorySlug?: string | null;
}) {
  const trimmed = query?.trim() ?? "";
  const category = demandCategoryFromSlug(categorySlug);
  const tracked = useRef(false);

  // Save the search itself — no form needed. Waits for the results to settle
  // so half-typed words ("brai" on the way to "brainrot") are not counted.
  useEffect(() => {
    if (trimmed.length < 2) return;
    const id = window.setTimeout(() => void logMissedSearch(trimmed, categorySlug), MISSED_SEARCH_SETTLE_MS);
    return () => window.clearTimeout(id);
  }, [trimmed, categorySlug]);

  useEffect(() => {
    if (tracked.current) return;
    tracked.current = true;
    track({ event: "demand_survey_shown", survey_key: "empty_search" });
  }, []);

  return (
    <div className="mt-10 w-full max-w-xl rounded-xl border border-border bg-card p-5 text-left">
      <div className="mb-4 flex items-start gap-3">
        <div className="rounded-lg bg-primary/10 p-2">
          <MessageSquarePlus className="h-5 w-5 text-primary" aria-hidden />
        </div>
        <div>
          <p className="font-display text-lg font-semibold leading-tight">
            {trimmed ? `Want "${trimmed}"? Tell us.` : "Tell us what you were looking for"}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            We use these answers to decide which assets to bring to the marketplace next.
          </p>
        </div>
      </div>
      {/* Keyed so a new search starts a fresh form. */}
      <DemandSurveyForm
        key={`${trimmed}|${categorySlug ?? ""}`}
        surveyKey="empty_search"
        questions={["text", "price"]}
        initialCategories={category ? [category] : []}
        prompts={{
          text: "What exactly do you need?",
          price: "What would you pay for it?",
        }}
        textPlaceholder={trimmed ? `More about "${trimmed}" — features, style, engine version…` : undefined}
        context={{ query: trimmed || undefined, category: categorySlug ?? undefined }}
        submitLabel="Send request"
      />
    </div>
  );
}
